import { AmbientLight, DirectionalLight, HemisphereLight, SpotLight, Vector3 } from 'three';

import type { LightingManagerConfig, LightingPreset, HighlightSpotlightConfig } from './types';
import { DEFAULT_LIGHTING_PRESET } from './types';
import { applyCustomSkyBrightnessCfn } from './functions/apply-custom-sky-brightness.cfn';
import { initializeCfn } from './functions/initialize.cfn';
import { enableHighlightCfn } from './functions/enable-highlight.cfn';
import { disposeCfn } from './functions/dispose.cfn';
import { restoreDefaultBrightnessCfn } from './functions/restore-default-brightness.cfn';

/**
 * LightingManager handles all scene lighting:
 * - Ambient light (global illumination)
 * - Directional light (main shadow-casting light)
 * - Hemisphere light (sky/ground color gradient)
 * - Highlight spotlight (for object highlighting)
 *
 * @example
 * ```typescript
 * const lightingManager = new LightingManager({
 *   scene: scene,
 *   shadowsEnabled: true
 * });
 *
 * lightingManager.initialize();
 *
 * // Highlight an object
 * lightingManager.setHighlightTarget(objectCenter, faceNormal);
 *
 * // Cleanup
 * lightingManager.dispose();
 * ```
 */
export class LightingManager {
	#config: LightingManagerConfig;
	#ambientLight: AmbientLight | null = null;
	#directionalLight: DirectionalLight | null = null;
	#hemisphereLight: HemisphereLight | null = null;
	#highlightSpotLight: SpotLight | null = null;

	// Store original directional light state for highlight mode
	#directionalLightOriginalIntensity = 2.0;
	#directionalLightOriginalVisible = true;

	constructor(config: LightingManagerConfig) {
		this.#config = config;
	}

	// ── Internal state for cfn access ────────────────────────────────────────
	/** @internal */ get internalConfig(): LightingManagerConfig {
		return this.#config;
	}
	/** @internal */ get internalAmbientLight(): AmbientLight | null {
		return this.#ambientLight;
	}
	/** @internal */ set internalAmbientLight(light: AmbientLight | null) {
		this.#ambientLight = light;
	}
	/** @internal */ get internalDirectionalLight(): DirectionalLight | null {
		return this.#directionalLight;
	}
	/** @internal */ set internalDirectionalLight(light: DirectionalLight | null) {
		this.#directionalLight = light;
	}
	/** @internal */ get internalHemisphereLight(): HemisphereLight | null {
		return this.#hemisphereLight;
	}
	/** @internal */ set internalHemisphereLight(light: HemisphereLight | null) {
		this.#hemisphereLight = light;
	}
	/** @internal */ get internalHighlightSpotLight(): SpotLight | null {
		return this.#highlightSpotLight;
	}
	/** @internal */ set internalHighlightSpotLight(light: SpotLight | null) {
		this.#highlightSpotLight = light;
	}
	/** @internal */ get internalDirectionalLightOriginalIntensity(): number {
		return this.#directionalLightOriginalIntensity;
	}
	/** @internal */ set internalDirectionalLightOriginalIntensity(intensity: number) {
		this.#directionalLightOriginalIntensity = intensity;
	}
	/** @internal */ get internalDirectionalLightOriginalVisible(): boolean {
		return this.#directionalLightOriginalVisible;
	}
	/** @internal */ set internalDirectionalLightOriginalVisible(visible: boolean) {
		this.#directionalLightOriginalVisible = visible;
	}

	/**
	 * Get the ambient light
	 */
	get ambientLight(): AmbientLight | null {
		return this.#ambientLight;
	}

	/**
	 * Get the directional light
	 */
	get directionalLight(): DirectionalLight | null {
		return this.#directionalLight;
	}

	/**
	 * Get the hemisphere light
	 */
	get hemisphereLight(): HemisphereLight | null {
		return this.#hemisphereLight;
	}

	/**
	 * Get the highlight spotlight
	 */
	get highlightSpotLight(): SpotLight | null {
		return this.#highlightSpotLight;
	}

	/**
	 * Initialize lighting with default preset
	 */
	initialize(preset: LightingPreset = DEFAULT_LIGHTING_PRESET): void {
		initializeCfn(this, { preset });
	}

	/**
	 * Set ambient light intensity
	 */
	setAmbientIntensity(intensity: number): void {
		if (this.#ambientLight) {
			this.#ambientLight.intensity = intensity;
		}
	}

	/**
	 * Set directional light intensity
	 */
	setDirectionalIntensity(intensity: number): void {
		if (this.#directionalLight) {
			this.#directionalLight.intensity = intensity;
		}
	}

	/**
	 * Set directional light position
	 */
	setDirectionalPosition(position: Vector3): void {
		if (this.#directionalLight) {
			this.#directionalLight.position.copy(position);
		}
	}

	/**
	 * Set directional light target
	 */
	setDirectionalTarget(target: Vector3): void {
		if (this.#directionalLight) {
			this.#directionalLight.target.position.copy(target);
		}
	}

	/**
	 * Update hemisphere light position (typically to scene center)
	 */
	setHemispherePosition(position: Vector3): void {
		if (this.#hemisphereLight) {
			this.#hemisphereLight.position.copy(position);
		}
	}

	/**
	 * Enable highlight mode (dims directional light, shows spotlight)
	 */
	enableHighlight(position: Vector3, target: Vector3, config?: HighlightSpotlightConfig): void {
		enableHighlightCfn(this, { position, target, config });
	}

	/**
	 * Update highlight spotlight position and target
	 */
	updateHighlight(position: Vector3, target: Vector3): void {
		if (this.#highlightSpotLight) {
			this.#highlightSpotLight.position.copy(position);
			this.#highlightSpotLight.target.position.copy(target);
		}
	}

	/**
	 * Disable highlight mode (restores directional light)
	 */
	disableHighlight(): void {
		// Restore directional light
		if (this.#directionalLight) {
			this.#directionalLight.intensity = this.#directionalLightOriginalIntensity;
			this.#directionalLight.visible = this.#directionalLightOriginalVisible;
		}

		// Hide spotlight
		if (this.#highlightSpotLight) {
			this.#highlightSpotLight.visible = false;
			this.#highlightSpotLight.intensity = 0;
		}
	}

	/**
	 * Check if highlight is active
	 */
	get isHighlightActive(): boolean {
		return this.#highlightSpotLight?.visible ?? false;
	}

	/**
	 * Enable/disable shadows
	 */
	setShadowsEnabled(enabled: boolean): void {
		if (this.#directionalLight) {
			this.#directionalLight.castShadow = enabled;
		}
	}

	/**
	 * Adjust scene lighting to match the perceived brightness of a custom sky image.
	 * Only intended for user-uploaded custom sky textures, not built-in presets.
	 * @param brightness average brightness of the custom sky image (0-1)
	 */
	applyCustomSkyBrightness (brightness: number): void {
		applyCustomSkyBrightnessCfn(this, { brightness });
	}

	/**
	 * Restore all light intensities to the default preset values.
	 * Called when switching away from a custom sky texture back to a built-in preset.
	 */
	restoreDefaultBrightness (): void {
		restoreDefaultBrightnessCfn(this, {});
	}

	/**
	 * Dispose of all lights
	 */
	dispose(): void {
		disposeCfn(this, {});
	}
}
